import { Box, Button, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { FormFileData } from 'pages/Contracts/Components/Forms/FormFileData'
import {
    ForwardRefRenderFunction,
    Ref,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react'
import { useForm } from 'react-hook-form'
import { schema } from './schema'
import './style.css'
import { FormFileDataProps, FormModalSignature } from './types'

interface PropsModalSignature {
    onSubmit: (value: FormModalSignature) => void // Agora passará apenas o arquivo
    onCancel: () => void
    refModal: Ref<ModalBuildHandle>
    allowZip?: boolean
}

const ModalSignatureFC: ForwardRefRenderFunction<any, PropsModalSignature> = (
    { onCancel, onSubmit, refModal, allowZip },
    signatureModalControllerRef
) => {
    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm<FormModalSignature>({
        resolver: yupResolver(schema)
    })

    const toast = useToast()
    const refFormFileData = useRef<any>(null)
    const [file, setFile] = useState<FormFileDataProps>()

    // Função para resetar o estado interno (chamada via Ref pelo pai)
    useImperativeHandle(signatureModalControllerRef, () => ({
        initialState() {
            reset()
            setFile(undefined)
            refFormFileData?.current?.removeFile()
        }
    }))

   const onSend = async (formData: FormModalSignature) => {
        const base64Data = file?.anexo?.base64;
        const fileName = file?.anexo?.nome || 'documento_assinado.pdf';

        if (!base64Data) {
            toast({
                title: 'Atenção',
                description: 'É necessário selecionar um arquivo PDF primeiro.',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
            return;
        }

        const base64ToFile = (base64: string, filename: string) => {
            try {
                const arr = base64.split(',');
                const mimeMatch = arr[0].match(/:(.*?);/);
                const mime = mimeMatch ? mimeMatch[1] : 'application/pdf';
                const bstr = atob(arr.length > 1 ? arr[1] : arr[0]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) { u8arr[n] = bstr.charCodeAt(n); }
                return new File([u8arr], filename, { type: mime });
            } catch (error) {
                return null;
            }
        };

        const arquivoReconstruido = base64ToFile(base64Data, fileName);

        if (!arquivoReconstruido) {
            toast({ title: 'Erro', description: 'Falha ao processar arquivo.', status: 'error' });
            return;
        }

        const dataToSubmit: FormModalSignature = {
            ...formData,
            file: {
                anexo: {
                    file: arquivoReconstruido,
                    base64: base64Data,
                    nome: fileName
                }
            }
        };

        // 2. Use AWAIT aqui! Isso faz o isSubmitting do useForm ficar true 
        // até que a função do pai termine.
        await onSubmit(dataToSubmit); 
    };

    useEffect(() => {
        // Verifique se a modal mudou para estado 'fechado'
        // Se você não tiver acesso ao 'isOpen' aqui, 
        // a melhor opção é a desmontagem no pai (item 1).
    }, []);

    const handleCancel = () => {
        reset();
        setFile(undefined);
        if (refFormFileData.current?.removeFile) {
            refFormFileData.current.removeFile();
        }
        onCancel();
    };
    return (
        <ModalBuild
            ref={refModal}
            closeOnOverlayClick={false}
            title="Assinatura Digital ICP-Brasil"
            size="2xl"
            onCancel={handleCancel}
        >
            <form onSubmit={handleSubmit(onSend)}>
                <Layout type="container" px={8} py={8} full display="grid" gap={8}>

                    <div className="modal-signature-form">
                        <FormFileData
                            ref={refFormFileData}
                            hideAttachmentIcon={true}
                            getFileData={e => setFile(e)}
                            allowZip={allowZip}
                        />
                    </div>

                    <Box display="flex" alignItems="center" justifyContent="center" gridGap={4}>
                        <Button 
                            variant="outline" 
                            onClick={handleCancel}
                            isDisabled={isSubmitting} // Desabilita o cancelar durante o processo
                        >
                            Cancelar
                        </Button>
                        <Button
                            colorScheme="primary"
                            type="submit"
                            isLoading={isSubmitting}      // Ativa o spinner
                            loadingText="Assinando..."    // O "Aguarde" que você pediu
                        >
                            Assinar Documento
                        </Button>
                    </Box>
                </Layout>
            </form>
        </ModalBuild>
    )
}

export const ModalSignature = forwardRef(ModalSignatureFC)